"use client";

/**
 * Input 컴포넌트
 * @description 라벨 + 필드 + 헬퍼 한 벌. 앱 DS `Input` 의 웹 포팅 — 앱 `Input.design.md` 가
 *   원본이다. 상태는 prop 이 아니다. rest·focused·filled 는 스스로 정해지고 호출부는 `error`
 *   와 `disabled` 만 준다. `error` 는 메시지가 아니라 상태라서 문구는 `helper` 로 간다.
 * @example
 * <Input label="Name" value={name} onValueChange={setName} error={!!msg} helper={msg} />
 */
import { memo, useId } from "react";

import { inputStyles } from "./Input.styles";
import type { InputProps } from "./Input.types";

const Input = memo<InputProps>(
  ({
    value,
    onValueChange,
    label,
    labelSuffix,
    helper,
    error = false,
    disabled = false,
    rows = 1,
    leadingIcon,
    followingIcon,
    onFollowingClick,
    followingLabel,
    id,
    ...rest
  }) => {
    const generatedId = useId();
    const inputId = id ?? generatedId;
    const helperId = `${inputId}-helper`;
    const multiline = rows > 1;
    const axis = { disabled, error };

    return (
      <div className={inputStyles.root}>
        {label ? (
          <div className={inputStyles.labelRow}>
            <label htmlFor={inputId} className={inputStyles.label(axis)}>
              {label}
            </label>
            {labelSuffix}
          </div>
        ) : null}

        <div className={inputStyles.box({ ...axis, multiline })}>
          {leadingIcon}
          {multiline ? (
            <textarea
              id={inputId}
              value={value}
              disabled={disabled}
              rows={rows}
              aria-invalid={error || undefined}
              aria-describedby={helper ? helperId : undefined}
              onChange={(event) => onValueChange(event.target.value)}
              className={inputStyles.field({ error, multiline })}
              placeholder={rest.placeholder}
            />
          ) : (
            <input
              id={inputId}
              value={value}
              disabled={disabled}
              aria-invalid={error || undefined}
              aria-describedby={helper ? helperId : undefined}
              onChange={(event) => onValueChange(event.target.value)}
              className={inputStyles.field({ error, multiline })}
              {...rest}
            />
          )}
          {followingIcon ? (
            onFollowingClick ? (
              <button
                type="button"
                onClick={onFollowingClick}
                aria-label={followingLabel}
                disabled={disabled}
                className={inputStyles.followingButton}
              >
                {followingIcon}
              </button>
            ) : (
              <span aria-hidden className={inputStyles.followingDecoration}>
                {followingIcon}
              </span>
            )
          ) : null}
        </div>

        {helper ? (
          <p id={helperId} className={inputStyles.helper(axis)}>
            {helper}
          </p>
        ) : null}
      </div>
    );
  },
);

Input.displayName = "Input";

export default Input;
